/**
 * SharePoint Domain - Path Builders
 * Bygger upp SharePoint-sökvägar baserat på kund, projekt och mapp-typ
 *
 * Kundmappen heter efter AVTALSPARTEN, alltså föreningen för en BRF och
 * personen för en privatperson. Namnet måste bli byte-identiskt med backendens
 * byggKundmappNamn i sharepointService.js — den skapade mappen, och hittar
 * ingen matchning om strängarna skiljer sig på ett enda tecken.
 *
 * Använd därför alltid hamtaBestallarnamn här, aldrig firstName + lastName.
 * Kundobjektet som skickas in MÅSTE ha med kundtyp och foreningsnamn, annars
 * faller det tyst tillbaka på kontaktpersonens namn och mappen hittas inte.
 */

import { hamtaBestallarnamn } from '../../../utils/nameUtils';

/**
 * Bygger sökväg för Underlag-mappen
 * @param {Object} customer - Kundobjekt med namn och projekt-info
 * @param {string} projectName - Projektnamn (t.ex. #12345)
 * @param {boolean} isPUMode - Om det är PU-läge (projekteringsunderlag)
 * @returns {string} SharePoint-sökväg
 */
export const buildUnderlagPath = (customer, projectName, isPUMode = false) => {
  if (!customer) return '';
  
  const fullName = hamtaBestallarnamn(customer);
  
  // PU-läge: Sökväg med projekt-ID inom parentes
  if (isPUMode || !projectName) {
    // Kräver att projectId finns - customer._id är ett MongoDB ObjectId och ska inte användas
    if (!customer.projectId) {
      console.error('SharePoint pathBuilders: customer.projectId saknas för PU-läge');
      return '';
    }
    const folderName = `${fullName} (#${customer.projectId})`;
    return `General/Leadme/${folderName}/Underlag/Underlag-delad`;
  }
  
  // Projekt-läge: Befintlig sökväg med år och projekt-ID
  const projectYear = customer.projectYear || new Date().getFullYear().toString();
  const folderName = `${fullName} (${projectName})`;
  return `General/Leadme/- Ja/${projectYear}/${folderName}/Underlag/Underlag-delad`;
};

/**
 * Bygger sökväg för Avtal-mappen
 * @param {Object} customer - Kundobjekt med namn och projekt-info
 * @param {string} projectName - Projektnamn (t.ex. #12345)
 * @param {string} userRole - Användarens roll (bestämmer root-mapp)
 * @returns {string} SharePoint-sökväg
 *
 * Root-mappar per roll:
 * - byggkonsult: Avtal/ (full åtkomst)
 * - byggare: Avtal/Avtal-delad/
 * - kund: Avtal/Avtal-delad/
 */
export const buildAvtalPath = (customer, projectName, userRole) => {
  if (!customer || !projectName) return '';

  const projectYear = customer.projectYear || new Date().getFullYear().toString();
  const fullName = hamtaBestallarnamn(customer);
  const folderName = `${fullName} (${projectName})`;
  const basePath = `General/Leadme/- Ja/${projectYear}/${folderName}/Avtal`;

  // Olika root-mappar baserat på roll
  switch (userRole) {
    case 'byggare':
    case 'kund':
      return `${basePath}/Avtal-delad`;
    case 'byggkonsult':
    case 'inredningsarkitekt':
    case 'inredare':
    default:
      return basePath;
  }
};

/**
 * Bygger sökväg för IA-ritningar-mappen
 * @param {Object} customer - Kundobjekt med namn och projekt-info
 * @param {string} projectName - Projektnamn (t.ex. #12345)
 * @returns {string} SharePoint-sökväg
 */
export const buildIARitningarPath = (customer, projectName) => {
  if (!customer || !projectName) return '';

  const projectYear = customer.projectYear || new Date().getFullYear().toString();
  const fullName = hamtaBestallarnamn(customer);
  const folderName = `${fullName} (${projectName})`;
  return `General/Leadme/- Ja/${projectYear}/${folderName}/Underlag/Underlag-delad/IA Ritningar`;
};

/**
 * Bygger sökväg för Dokumentation-mappen
 * @param {Object} customer - Kundobjekt med namn och projekt-info
 * @param {string} projectName - Projektnamn (t.ex. #12345)
 * @returns {string} SharePoint-sökväg
 */
export const buildDokumentationPath = (customer, projectName) => {
  if (!customer || !projectName) return '';

  const projectYear = customer.projectYear || new Date().getFullYear().toString();
  const fullName = hamtaBestallarnamn(customer);
  const folderName = `${fullName} (${projectName})`;
  return `General/Leadme/- Ja/${projectYear}/${folderName}/Dokumentation`;
};

/**
 * Bygger sökväg baserat på mapp-typ
 * @param {string} folderType - 'underlag', 'avtal', 'ia_ritningar' eller 'dokumentation'
 * @param {Object} customer - Kundobjekt
 * @param {string} projectName - Projektnamn
 * @param {string} userRole - Användarens roll
 * @param {boolean} isPUMode - Om PU-läge (endast för underlag)
 * @returns {string} SharePoint-sökväg
 */
export const buildFolderPath = (folderType, customer, projectName, userRole, isPUMode = false) => {
  switch (folderType) {
    case 'underlag':
      return buildUnderlagPath(customer, projectName, isPUMode);
    case 'avtal':
      return buildAvtalPath(customer, projectName, userRole);
    case 'ia_ritningar':
      return buildIARitningarPath(customer, projectName);
    case 'dokumentation':
      return buildDokumentationPath(customer, projectName);
    default:
      throw new Error(`Unknown folder type: ${folderType}`);
  }
};

/**
 * Kontrollerar om en sökväg är root-mappen
 * @param {string} currentPath - Nuvarande sökväg
 * @param {string} rootPath - Root-sökvägen
 * @returns {boolean} true om vi är i root
 */
export const isRootPath = (currentPath, rootPath) => currentPath === rootPath;

